import { getPortalScope } from 'utils/portalScope';
import { resolveApiUrlRoot } from 'utils/apiUrl';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  AUTH_EXPIRED_EVENT,
  AUTH_REFRESHED_EVENT,
  AUTH_REFRESH_LOCK_NAME,
  AUTH_REFRESH_RETRY_MS,
  AUTH_SESSION_STORAGE_KEY,
  clearBrowserSession,
  getProactiveRefreshDelay,
  isAccessAuthError,
  isConfirmedSessionEnd,
  isLoginRequest,
  isLogoutRequest,
  isRefreshRace,
  isRefreshRequest,
  readBrowserSession,
  shouldAttemptRefresh,
  storeBrowserSession,
} from 'utils/authSession';

export const getApiUrlRoot = () => resolveApiUrlRoot(process.env.REACT_APP_API_URL_ROOT);

const API_URL_ROOT = getApiUrlRoot();
const API_AUTH_REFRESH_TOKEN = 'auth/refresh-token';
const API_ERROR_FLAG = '__BW_API_ERROR__';
const PORTAL_PAGE_ID = uuidv4();
let refreshTokenRequest = null;
let proactiveRefreshTimer = null;
let browserLifecycleInitialized = false;

const expireBrowserSession = (reason = 'SESSION_EXPIRED') => {
  clearBrowserSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { reason } }));
  }
};

export const isHttpClientApiError = (error) => Boolean(error && typeof error === 'object' && error[API_ERROR_FLAG]);

const markApiError = (error, fallbackMessage = 'Đã xảy ra lỗi khi kết nối API.') => {
  const apiError =
    error && typeof error === 'object'
      ? error
      : {
          message: error || fallbackMessage,
        };

  if (!apiError.message && fallbackMessage) {
    apiError.message = fallbackMessage;
  }

  Object.defineProperty(apiError, API_ERROR_FLAG, {
    value: true,
    enumerable: false,
    configurable: true,
  });

  return apiError;
};

const httpClient = () => {
  /**
   * Chuyen doi, format du lieu tu api tra ve cho dong bo
   * @param {Object|null} data
   * @return {Object|null}
   */
  const convertApiErrData = (data) => {
    if (data) {
      let { errors: errArr } = data;

      if (Array.isArray(errArr) && errArr.length) {
        let errors = [];
        errArr.forEach((err) => {
          errors = errors.concat(err.messages || []);
        });
        Object.assign(data, { errors });
      }
    }
    return data;
  };
  const instance = axios.create({
    baseURL: API_URL_ROOT,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const scheduleProactiveRefresh = (tokenExpireAt, delayOverride) => {
    if (typeof window === 'undefined') return;
    if (proactiveRefreshTimer) window.clearTimeout(proactiveRefreshTimer);
    const delay = delayOverride ?? getProactiveRefreshDelay(tokenExpireAt);
    if (!tokenExpireAt && delayOverride === undefined) return;

    proactiveRefreshTimer = window.setTimeout(async () => {
      try {
        await refreshBrowserSession({ observedRefreshedAt: readBrowserSession().refreshedAt });
      } catch (error) {
        if (isConfirmedSessionEnd(error)) {
          expireBrowserSession(error?.response?.data?.errors?.code);
          return;
        }
        // Network, database and Redis outages are recoverable. Keep the current
        // screen and retry instead of converting an infrastructure fault to logout.
        scheduleProactiveRefresh(0, AUTH_REFRESH_RETRY_MS);
      }
    }, delay);
  };

  const captureTokenExpiry = (data) => {
    const tokenExpireAt = Number(data?.tokenExpireAt);
    if (!Number.isFinite(tokenExpireAt) || tokenExpireAt <= 0) return;
    storeBrowserSession(tokenExpireAt);
    scheduleProactiveRefresh(tokenExpireAt);
  };

  const performRefresh = async () => {
    return instance.post(API_AUTH_REFRESH_TOKEN, {}, { _skipAuthRefresh: true });
  };

  const waitForPeerRefresh = (previousRefreshedAt, timeoutMs = 1000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();
      const check = () => {
        const session = readBrowserSession();
        if (session.refreshedAt > Number(previousRefreshedAt || 0) || Date.now() - startedAt >= timeoutMs) {
          resolve(session);
          return;
        }
        window.setTimeout(check, 40);
      };
      check();
    });

  const refreshWithBrowserLock = async (observedRefreshedAt = 0) => {
    const refreshIfStillNeeded = async () => {
      const currentSession = readBrowserSession();
      if (currentSession.refreshedAt > Number(observedRefreshedAt || 0)) return currentSession;
      return performRefresh();
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request(AUTH_REFRESH_LOCK_NAME, refreshIfStillNeeded);
    }
    return refreshIfStillNeeded();
  };

  const refreshBrowserSession = ({ observedRefreshedAt = 0 } = {}) => {
    if (!refreshTokenRequest) {
      refreshTokenRequest = refreshWithBrowserLock(observedRefreshedAt).finally(() => {
        refreshTokenRequest = null;
      });
    }
    return refreshTokenRequest;
  };

  instance.interceptors.request.use(
    (config) => {
      config.headers['X-Portal-Page-Id'] = PORTAL_PAGE_ID;
      config._authRefreshedAt = readBrowserSession().refreshedAt || 0;
      const portalScope = getPortalScope();
      if (portalScope.company_id) {
        config.headers['X-Company-Id'] = portalScope.company_id;
      }
      if (portalScope.business_id) {
        config.headers['X-Business-Id'] = portalScope.business_id;
      }
      if (portalScope.store_id) {
        config.headers['X-Store-Id'] = portalScope.store_id;
      }
      return config;
    },
    (error) => {
      return Promise.reject(markApiError(error));
    },
  );

  instance.interceptors.response.use(
    (response) => {
      const { config } = response;

      if (config.responseType && config.responseType === 'blob') {
        return response;
      }
      let { data: apiData } = response.data;
      if (isLoginRequest(config.url) || isRefreshRequest(config.url)) captureTokenExpiry(apiData);
      if (isLogoutRequest(config.url)) {
        clearBrowserSession();
        scheduleProactiveRefresh(0);
      }
      if (config.method === 'delete') {
        if (document.getElementById('data-table-select')) document.getElementById('data-table-select')?.click();
      }
      return apiData;
    },
    async (err) => {
      const originalConfig = err.config;
      let { data: apiData = {}, status } = err.response || {};
      apiData = Object.assign(apiData, { status });
      if (!apiData.status) {
        return Promise.reject(markApiError(apiData, 'Vui lòng kiểm tra lại kết nối.'));
      }
      if (status === 501 || status === 400 || status === 404) {
        return Promise.reject(markApiError(convertApiErrData(apiData)));
      }
      if (
        shouldAttemptRefresh({
          status,
          url: originalConfig?.url,
          retried: originalConfig?._retry,
          skipAuthRefresh: originalConfig?._skipAuthRefresh,
        })
      ) {
        originalConfig._retry = true;
        try {
          await refreshBrowserSession({ observedRefreshedAt: originalConfig._authRefreshedAt });
          delete originalConfig.headers.Authorization;
          return instance(originalConfig);
        } catch (refreshError) {
          // Another tab may have won single-use refresh rotation and already
          // installed the new HttpOnly cookies. Retry once with that session.
          if (isRefreshRace(refreshError)) {
            const peerSession = await waitForPeerRefresh(originalConfig._authRefreshedAt);
            if (peerSession.refreshedAt > originalConfig._authRefreshedAt) {
              delete originalConfig.headers.Authorization;
              return instance(originalConfig);
            }
          }
          if (isConfirmedSessionEnd(refreshError)) {
            expireBrowserSession(refreshError?.response?.data?.errors?.code);
          }
          return Promise.reject(markApiError(refreshError?.response?.data || refreshError));
        }
      }
      if (status === 401 && originalConfig?._retry && isAccessAuthError(apiData)) {
        expireBrowserSession(apiData?.errors?.code);
      }
      return Promise.reject(markApiError(apiData));
    },
  );

  if (typeof window !== 'undefined' && !browserLifecycleInitialized) {
    browserLifecycleInitialized = true;
    const rescheduleFromStorage = (event) => {
      if (event?.key && event.key !== AUTH_SESSION_STORAGE_KEY) return;
      const session = readBrowserSession();
      scheduleProactiveRefresh(session.tokenExpireAt);
      if (event?.key === AUTH_SESSION_STORAGE_KEY && event.oldValue && !event.newValue) {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { reason: 'SESSION_ENDED' } }));
      }
    };
    window.addEventListener('storage', rescheduleFromStorage);
    window.addEventListener(AUTH_REFRESHED_EVENT, rescheduleFromStorage);
    window.addEventListener('focus', rescheduleFromStorage);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') rescheduleFromStorage();
    });
    const existingSession = readBrowserSession();
    if (existingSession.tokenExpireAt) scheduleProactiveRefresh(existingSession.tokenExpireAt);
  }
  return instance;
};

export default httpClient();
